import React, { useEffect, useState } from 'react';
import { CheckCircle, EnvelopeSimple, Info, PencilSimple, Question, UserCircleCheck } from '@phosphor-icons/react';
import type { Ticket } from '../types';
import { supabase } from '../../../../lib/supabase/client';
import { ladeVorgangsarten, type Vorgangsart } from '../../../../lib/vorgaenge/vorgangsarten';
import { fehlendeAngaben, fortschritt, leseTicketAngaben, naechsterPunkt, rueckfrageText, setzeAngabe, type GespeicherteAngabe } from '../../../../lib/vorgaenge/uebersicht';
import { baueAktionsKontext } from '../../../../lib/aktionen/kontext';
import { speichereAktionen } from '../../../../lib/aktionen/api';

type VorgangsTicket = Ticket & { vorgangsart_id?: string | null; vorgang_angaben?: unknown };

/** Hat das Ticket einen Vorgang? (für den Einbau: Widget nur dann zeigen) */
export const hatVorgang = (t: Ticket): boolean => !!(t as VorgangsTicket).vorgangsart_id;

const STATUS: Record<GespeicherteAngabe['status'], { icon: typeof CheckCircle; farbe: string; text: string }> = {
  mail: { icon: CheckCircle, farbe: 'var(--ok)', text: 'erfasst' },
  app: { icon: UserCircleCheck, farbe: 'var(--ok)', text: 'kennt die App' },
  intern: { icon: Info, farbe: 'var(--ink3)', text: 'prüft das Team' },
  fehlt: { icon: Question, farbe: 'var(--warn)', text: 'fehlt' },
};

/**
 * #489 Etappe V4: Inhalt des Widgets „Vorgang“ im Ticket-Detail (ohne eigene Karte/Überschrift –
 * die Hülle setzt die Detailseite). Zeigt Vorgangsart + Erklärung, Fortschritt, nächsten Schritt
 * und die Angaben (von Hand nachtragbar). „Fehlende Angaben anfragen“ legt einen Antwortentwurf an,
 * der wie jede Aktion erst nach Freigabe versendet wird.
 */
export default function VorgangWidget({ ticket, onUpdate }: { ticket: Ticket; onUpdate?: () => void }) {
  const t = ticket as VorgangsTicket;
  const [art, setArt] = useState<Vorgangsart | null>(null);
  const [bearbeite, setBearbeite] = useState<string | null>(null);
  const [wert, setWert] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<{ text: string; fehler?: boolean } | null>(null);

  useEffect(() => {
    if (!t.vorgangsart_id) return;
    ladeVorgangsarten().then(a => setArt(a.find(x => x.id === t.vorgangsart_id) ?? null)).catch(() => setArt(null));
  }, [t.vorgangsart_id]);

  if (!t.vorgangsart_id) return null;

  const angaben = leseTicketAngaben(t.vorgang_angaben);
  const fehlt = fehlendeAngaben(t.vorgang_angaben);
  const f = fortschritt(t.beschreibung);
  const naechster = naechsterPunkt(t.beschreibung);

  const speichereAngabe = async (schluessel: string) => {
    setLaeuft(true); setMeldung(null);
    const neu = setzeAngabe(t.vorgang_angaben, schluessel, wert);
    const { error } = await supabase.from('tickets').update({ vorgang_angaben: neu }).eq('id', t.id);
    setLaeuft(false);
    if (error) { setMeldung({ text: `Nicht gespeichert: ${error.message}`, fehler: true }); return; }
    setBearbeite(null);
    onUpdate?.();
  };

  const anfragen = async () => {
    if (!fehlt.length) return;
    setLaeuft(true); setMeldung(null);
    try {
      const ctx = await baueAktionsKontext({
        id: t.id, titel: t.titel, ticket_nummer: t.ticket_nummer, objekt_id: t.objekt_id, mieter_id: t.mieter_id, einheit_id: t.einheit_id ?? null,
        kategorie_id: t.kategorie_id, prioritaet: t.prioritaet, absender_typ: t.absender_typ ?? null, erkannte_aufgabe: t.erkannte_aufgabe ?? null,
        naechster_schritt: t.naechster_schritt ?? null, dienstleister_vorschlag_id: t.dienstleister_vorschlag_id ?? null, ki_confidence: t.ki_confidence ?? null,
        ist_rechtlich_sensibel: !!t.ist_rechtlich_sensibel,
      });
      const empfaenger = ctx.stammdaten?.mieter?.email ?? ctx.stammdaten?.absenderEmail ?? null;
      if (!empfaenger) throw new Error('Keine E-Mail-Adresse des Absenders gefunden.');
      const { data: { user } } = await supabase.auth.getUser();
      await speichereAktionen([{
        typ: 'antwort_absender', freigabe_erforderlich: true, ist_rechtlich_sensibel: !!t.ist_rechtlich_sensibel,
        empfaenger, empfaenger_kontakt_id: null, betreff: `AW: ${t.titel}`, entwurf_text: rueckfrageText(t.ticket_nummer, fehlt),
        payload: { vorgang_rueckfrage: true }, ki_confidence: null,
      }], t.id, user?.id ?? null);
      await supabase.from('ticket_historie').insert({ ticket_id: t.id, benutzer_id: user?.id ?? null, aktion: 'vorgang_rueckfrage', neuer_wert: `Rückfrage vorbereitet: ${fehlt.join(', ')}` });
      setMeldung({ text: 'Rückfrage als Entwurf angelegt – bitte bei den Aktionen prüfen und freigeben.' });
      onUpdate?.();
    } catch (e) {
      setMeldung({ text: e instanceof Error ? e.message : 'Rückfrage fehlgeschlagen', fehler: true });
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ gap: 10, fontSize: 13 }}>
      <div className="flex flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>{art?.name ?? 'Vorgang'}</span>
        {art?.erklaerung && <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--ink3)' }}>{art.erklaerung}</span>}
      </div>

      {f.gesamt > 0 && (
        <div className="flex flex-col" style={{ gap: 4 }}>
          <span className="flex justify-between" style={{ fontSize: 12, color: 'var(--ink2)' }}><span>Checkliste</span><span className="immo-zahl">{f.erledigt}/{f.gesamt}</span></span>
          <span aria-hidden style={{ height: 6, borderRadius: 3, background: 'var(--fill2)', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${f.prozent}%`, background: f.prozent === 100 ? 'var(--ok)' : 'var(--acc)' }} />
          </span>
          {naechster && <span style={{ fontSize: 12, color: 'var(--ink2)' }}><b>Nächster Schritt:</b> {naechster}</span>}
        </div>
      )}

      {angaben.length > 0 && (
        <ul className="flex flex-col" style={{ margin: 0, padding: 0, listStyle: 'none', gap: 6 }}>
          {angaben.map(a => {
            const st = STATUS[a.status];
            const Icon = st.icon;
            const offen = bearbeite === a.schluessel;
            return (
              <li key={a.schluessel} className="flex flex-col" style={{ gap: 4 }}>
                <span className="flex items-start" style={{ gap: 6 }}>
                  <Icon size={14} weight="fill" color={st.farbe} className="shrink-0" style={{ marginTop: 2 }} />
                  <span className="flex-1 min-w-0" style={{ overflowWrap: 'anywhere' }}>
                    <b>{a.bezeichnung}</b>{a.wert ? `: ${a.wert}` : ''} <span style={{ color: 'var(--ink3)', fontSize: 12 }}>· {st.text}</span>
                  </span>
                  {!offen && (
                    <button type="button" aria-label={`${a.bezeichnung} bearbeiten`} onClick={() => { setBearbeite(a.schluessel); setWert(a.wert ?? ''); }}
                      className="immo-hover-flaeche flex items-center justify-center cursor-pointer shrink-0"
                      style={{ width: 26, height: 26, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
                      <PencilSimple size={13} />
                    </button>
                  )}
                </span>
                {offen && (
                  <span className="flex items-center" style={{ gap: 6, paddingLeft: 20 }}>
                    <input autoFocus value={wert} onChange={e => setWert(e.target.value)} aria-label={a.bezeichnung}
                      onKeyDown={e => { if (e.key === 'Enter') void speichereAngabe(a.schluessel); if (e.key === 'Escape') setBearbeite(null); }}
                      style={{ flex: 1, minWidth: 0, height: 34, padding: '0 10px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13 }} />
                    <button type="button" disabled={laeuft} onClick={() => { void speichereAngabe(a.schluessel); }} className="cursor-pointer"
                      style={{ height: 34, padding: '0 12px', borderRadius: 17, border: 'none', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 12, fontWeight: 800, opacity: laeuft ? 0.5 : 1 }}>Speichern</button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {fehlt.length > 0 && (
        <button type="button" disabled={laeuft} onClick={() => { void anfragen(); }} className="flex items-center justify-center cursor-pointer"
          style={{ height: 36, borderRadius: 18, border: 'none', gap: 6, fontSize: 12, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)', opacity: laeuft ? 0.5 : 1 }}>
          <EnvelopeSimple size={14} weight="bold" /> Fehlende Angaben anfragen ({fehlt.length})
        </button>
      )}
      {meldung && <span role={meldung.fehler ? 'alert' : 'status'} style={{ fontSize: 12, fontWeight: 700, color: meldung.fehler ? 'var(--bad)' : 'var(--ok)' }}>{meldung.text}</span>}
    </div>
  );
}
